// React imports
import React from 'react';
import PropTypes from 'prop-types';

// redux
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

// i18n
import { Translate, I18n } from 'react-redux-i18n';

// React Spectrum Components
import Textfield from '@react/react-spectrum/Textfield';
import Select from '@react/react-spectrum/Select';
import Button from '@react/react-spectrum/Button';
import Heading from '@react/react-spectrum/Heading';
import FieldLabel from '@react/react-spectrum/FieldLabel';
import Wait from '@react/react-spectrum/Wait';
import { Grid, GridRow, GridColumn } from '@react/react-spectrum/Grid';

// Local project components
import Progress from '../global/Progress';

// Redux actions
import { submitForm } from '../../actions/formActions';

// Styles
import '../css/PersonalInformation.scss';


class PersonalInformation extends React.Component {
  options = [
    { label: <Translate value="workflow.personal_information.role_accounting" />, value: 'Accounting' },
    { label: <Translate value="workflow.personal_information.role_administration" />, value: 'Administration' },
    { label: <Translate value="workflow.personal_information.role_development" />, value: 'Development' },
    { label: <Translate value="workflow.personal_information.role_executive" />, value: 'Executive' },
    { label: <Translate value="workflow.personal_information.role_finance" />, value: 'Finance' },
    { label: <Translate value="workflow.personal_information.role_helpdesk_support" />, value: 'Helpdesk Support' },
    { label: <Translate value="workflow.personal_information.role_it" />, value: 'IT' },
    { label: <Translate value="workflow.personal_information.role_marketing" />, value: 'Marketing' },
    { label: <Translate value="workflow.personal_information.role_owner" />, value: 'Owner' },
    { label: <Translate value="workflow.personal_information.role_partner_specialist" />, value: 'Partner Specialist' },
    { label: <Translate value="workflow.personal_information.role_pr_communications" />, value: 'PR/Communications' },
    { label: <Translate value="workflow.personal_information.role_professional_services" />, value: 'Professional Services' },
    { label: <Translate value="workflow.personal_information.role_sales_representative" />, value: 'Sales Representative' },
    { label: <Translate value="workflow.personal_information.role_sales_management" />, value: 'Sales Management' },
    { label: <Translate value="workflow.personal_information.role_system_presales_engineer" />, value: 'System/Pre-sales Engineer' },
    { label: <Translate value="workflow.personal_information.role_technical_support" />, value: 'Technical Support' },
    { label: <Translate value="workflow.personal_information.role_training" />, value: 'Training' },
    { label: <Translate value="workflow.personal_information.role_web_administration_master" />, value: 'Web Administration/Master' },
  ];

  submit = (values, dispatch) => {
    const { history } = this.props;
    return dispatch(submitForm(values, history));
  }

  checkValidationState = (value, meta) => {
    // Valid
    if (value && !meta.error) {
      return 'valid';
    }
    // Initial State
    if (meta.error && !meta.touched) {
      return null;
    }
    // Invalid
    if ((!value && meta.touched) || meta.error) {
      return 'invalid';
    }
    // Nothing
    return null;
  }

  checkSelectValidationState = (value, meta) => {
    // Valid
    if (value && !meta.error) {
      return false;
    }
    // Initial State or when user clicks --> then clicks out without selecting a value
    if ((meta.error && !meta.touched)) {
      return null;
    }
    // Invalid
    if ((!value && meta.touched) || meta.error) {
      return true;
    }
    // Nothing
    return null;
  }

  spectrumTextField = ({
    input,
    name,
    className,
    title,
    label,
    placeholder,
    id,
    disabled,
    meta,
  }) => (
    <div>
      <FieldLabel
        className="form-label"
        labelFor="textfield-0"
        label={label}
      />
      <Textfield
        onBlur={input.onBlur}
        onChange={input.onChange}
        onDragStart={input.onDragStart}
        onDrop={input.onDrop}
        onFocus={input.onFocus}
        value={input.value}
        marginEnd="20px"
        title={title}
        name={name}
        placeholder={placeholder}
        id={id}
        className={className}
        style={{ width: '95%' }}
        validationState={this.checkValidationState(input.value, meta)}
        autoComplete="off"
        autoCorrect="off"
        spellCheck="off"
        disabled={disabled}
      />
    </div>
  );

 spectrumSelectField = ({
   input,
   name,
   className,
   title,
   label,
   placeholder,
   id,
   meta,
 }) => (
   <div>
     <FieldLabel
       className="form-label"
       labelFor="textfield-0"
       label={label}
     />
     <Select
       onChange={input.onChange}
       onDragStart={input.onDragStart}
       onDrop={input.onDrop}
       onFocus={input.onFocus}
       closeOnSelect
       title={title}
       label={label}
       name={name}
       placeholder={placeholder}
       id={id}
       className={className}
       style={{ width: '95%' }}
       options={this.options}
       invalid={this.checkSelectValidationState(input.value, meta)}
       value={input.value}
       onBlur={() => {
         input.onBlur(input.value);
       }}
       required
     />
   </div>
 );

 renderLabel = (label) => {
   const translation = I18n.t(label);
   const required = `${translation} *`;
   return (
     required
   );
 }

 render() {
   const {
     handleSubmit, valid, pristine, submitting,
   } = this.props;

   return (
     <div data-testid="form-container" className="form-container">
       <Progress step={2} />
       <Grid variant="fluid">
         <GridRow reverse={false}>
           <GridColumn size={12}>
             <Heading>
               <Translate value="workflow.personal_information.header" />
             </Heading>
             <form
               className="personal-information"
               onSubmit={handleSubmit(this.submit)}
             >
               <GridRow reverse={false}>
                 <GridColumn size="auto">
                   <Field
                     data-testid="firstName"
                     name="firstName"
                     label={this.renderLabel('workflow.personal_information.first_name')}
                     component={this.spectrumTextField}
                     id="firstName"
                     disabled
                   />
                 </GridColumn>
                 <GridColumn size="auto">
                   <Field
                     data-testid="lastName"
                     name="lastName"
                     label={this.renderLabel('workflow.personal_information.last_name')}
                     component={this.spectrumTextField}
                     id="lastName"
                     disabled
                   />
                 </GridColumn>
               </GridRow>
               <GridRow reverse={false}>
                 <GridColumn size="auto">
                   <Field
                     data-testid="email"
                     name="email"
                     label={this.renderLabel('workflow.personal_information.email_address')}
                     component={this.spectrumTextField}
                     id="email"
                     disabled
                   />
                 </GridColumn>
                 <GridColumn size="auto">
                   <Field
                     data-testid="phone"
                     name="phone"
                     label={this.renderLabel('workflow.personal_information.phone_number')}
                     component={this.spectrumTextField}
                     id="phone"
                     placeholder={I18n.t('workflow.personal_information.phone_number_placeholder')}
                   />
                 </GridColumn>
               </GridRow>
               <GridRow reverse={false}>
                 <GridColumn size={6}>
                   <Field
                     data-testid="jobRole"
                     name="jobRole"
                     label={this.renderLabel('workflow.personal_information.job_role')}
                     component={this.spectrumSelectField}
                     id="jobRole"
                     placeholder={I18n.t('workflow.personal_information.job_role_placeholder')}
                   />
                 </GridColumn>
               </GridRow>
               <GridRow reverse={false}>
                 <GridColumn size={12}>
                   {submitting ? <Wait data-test-id="loading-spinner" size="large" /> : (
                     <Button data-testid="submit-button" className="submitButton" variant="cta" disabled={!valid || pristine || submitting}>
                       <Translate value="workflow.personal_information.submit_button_text" />
                     </Button>
                   )}

                 </GridColumn>
               </GridRow>
             </form>
           </GridColumn>
         </GridRow>
       </Grid>
     </div>
   );
 }
}

PersonalInformation.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  history: PropTypes.shape({
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object,
    push: PropTypes.func,
  }).isRequired,
};

PersonalInformation.defaultProps = {
  valid: false,
  pristine: false,
  submitting: false,
};

const myValidator = (values) => {
  const errors = {};
  if (!values.phone) {
    errors.phone = 'Phone number is required!';
  } else if (
    !/^[^-\sA-Za-z](\(?\+?[0-9]*\)?)?[0-9_\- ()]*(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/i.test(
      values.phone,
    )
  ) {
    errors.phone = 'Phone number is not valid!';
  }

  if (!values.jobRole) {
    errors.jobRole = 'Role is required!';
  }

  if (!values.terms) {
    errors.terms = 'Terms is required!';
  }

  return errors;
};

const mapStateToProps = (state) => ({
  initialValues: {
    firstName: window.adobeIMS.getUserProfile().first_name,
    lastName: window.adobeIMS.getUserProfile().last_name,
    email: window.adobeIMS.getUserProfile().email,
  },
  locale: state.i18n.locale,
});

const mapDispatchToProps = {
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'apcForm',
    validate: myValidator,
    destroyOnUnmount: false,
  })(PersonalInformation),
);
